import {
  Component,
  inject,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { M3ButtonComponent } from '@shared/button/m3-button.component';
import { AppUpdatesStore } from '../state/app-updates.store';
import { AppUpdatesLabels } from '../localization/app-updates.labels';
import { AppUpdatesAria } from '../localization/app-updates.aria';
import { formatVersionHash } from '../utils/version.utils';

@Component({
  selector: 'app-update-status',
  standalone: true,
  imports: [M3ButtonComponent],
  template: `
    <p class="visually-hidden" role="status">{{ statusAnnouncement() }}</p>
    @switch (updateStatus()) {
      @case ('downloading') {
        <div class="version-item update-downloading span-2">
          <div class="version-content">
            <div class="version-header">
              <span
                class="material-symbols-outlined version-icon downloading-icon spin"
                aria-hidden="true"
                >sync</span
              >
              <div class="version-info">
                <h4 class="version-label mat-title-small">
                  {{ downloadingLabel() }}
                </h4>
                <p class="version-secondary mat-body-medium">
                  {{ downloadingDescription() }}
                </p>
              </div>
            </div>
          </div>
        </div>
      }
      @case ('error') {
        <div class="version-item update-error span-2">
          <div class="version-content">
            <div class="version-header">
              <span class="material-symbols-outlined version-icon error-icon"
                >error</span
              >
              <div class="version-info">
                <h4 class="version-label mat-title-small">
                  Update-Prüfung fehlgeschlagen
                </h4>
                <p class="version-error mat-body-medium">
                  {{ appUpdatesStore.errorMessage() }}
                </p>
              </div>
            </div>
            <div class="version-action">
              <app-m3-button
                variant="outlined"
                size="sm"
                [iconLeft]="'sync'"
                [disabled]="isCheckForUpdateLoading()"
                (clicked)="appUpdatesStore.checkForUpdate()"
              >
                {{
                  isCheckForUpdateLoading() ? 'Prüfe...' : 'Erneut versuchen'
                }}
              </app-m3-button>
            </div>
          </div>
        </div>
      }
      @case ('update-available') {
        <div class="version-item update-available span-2">
          <div class="version-content">
            <div class="version-header">
              <span class="material-symbols-outlined version-icon update-icon"
                >new_releases</span
              >
              <div class="version-info">
                <h4 class="version-label mat-title-small">Update verfügbar</h4>
                <p class="version-value mat-body-large">
                  {{ currentVersionText() }} → {{ latestVersionText() }}
                </p>
              </div>
            </div>
            <div class="version-action">
              <app-m3-button
                variant="filled"
                size="sm"
                [iconLeft]="'refresh'"
                [disabled]="isActivateUpdateLoading()"
                (clicked)="appUpdatesStore.reloadWithUpdate()"
              >
                {{
                  isActivateUpdateLoading()
                    ? 'Wird aktualisiert...'
                    : 'Jetzt aktualisieren'
                }}
              </app-m3-button>
            </div>
          </div>
        </div>
      }
      @case ('no-update') {
        <div class="version-item span-2">
          <div class="version-content">
            <div class="version-header">
              <span class="material-symbols-outlined version-icon">info</span>
              <div class="version-info">
                <h4 class="version-label mat-title-small">
                  Keine Updates verfügbar
                </h4>
                <p class="version-secondary mat-body-medium">
                  Zuletzt geprüft: {{ lastCheckedText() }}
                </p>
              </div>
            </div>
            <div class="version-action">
              <app-m3-button
                variant="outlined"
                size="sm"
                [iconLeft]="'sync'"
                [disabled]="isCheckForUpdateLoading()"
                (clicked)="appUpdatesStore.checkForUpdate()"
              >
                {{
                  isCheckForUpdateLoading() ? 'Prüfe...' : 'Nach Updates suchen'
                }}
              </app-m3-button>
            </div>
          </div>
        </div>
      }
      @case ('disabled') {
        <div class="version-item sw-disabled span-2">
          <div class="version-header">
            <span class="material-symbols-outlined version-icon"
              >cloud_off</span
            >
            <div class="version-info">
              <h4 class="version-label mat-title-small">Updates deaktiviert</h4>
              <p class="version-secondary mat-body-medium">
                Automatische Update-Prüfung ist nicht verfügbar
              </p>
            </div>
          </div>
        </div>
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .version-item {
        padding: 20px;
        border: 1px solid var(--md-sys-color-outline-variant);
        border-radius: 12px;
        background: var(--md-sys-color-surface-container-low);
      }

      .version-item.span-2 {
        grid-column: span 2;
      }

      .version-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
      }

      .version-header {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        flex: 1;
      }

      .version-icon {
        font-size: 24px;
        color: var(--md-sys-color-primary);
        margin-top: 2px;
      }

      .version-info {
        flex: 1;
      }

      .version-action {
        display: flex;
        align-items: center;
      }

      .version-label {
        margin: 0 0 4px 0;
        color: var(--md-sys-color-on-surface-variant);
        font-weight: 500;
      }

      .version-value {
        margin: 0;
        color: var(--md-sys-color-on-surface);
        font-weight: 600;
      }

      .version-secondary {
        margin: 4px 0 0 0;
        color: var(--md-sys-color-on-surface-variant);
      }

      .sw-disabled {
        background: var(--md-sys-color-surface);
        border-color: var(--md-sys-color-outline-variant);
        border-style: dashed;
      }

      .sw-disabled .version-icon {
        opacity: 0.5;
        color: var(--md-sys-color-on-surface-variant);
      }

      .sw-disabled .version-label {
        color: var(--md-sys-color-on-surface-variant);
        font-weight: 400;
      }

      .sw-disabled .version-secondary {
        color: var(--md-sys-color-on-surface-variant);
        opacity: 0.8;
      }

      .update-available {
        border-color: var(--md-sys-color-tertiary);
        background: var(--md-sys-color-tertiary-container);
      }

      .update-icon {
        color: var(--md-sys-color-tertiary) !important;
      }

      .update-downloading {
        border-color: var(--md-sys-color-primary);
        background: var(--md-sys-color-primary-container);
      }

      .downloading-icon {
        color: var(--md-sys-color-primary) !important;
      }

      .spin {
        animation: spin 1.5s linear infinite;
      }

      @keyframes spin {
        from {
          transform: rotate(0deg);
        }
        to {
          transform: rotate(-360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .spin {
          animation: none;
        }
      }

      .update-error {
        border-color: var(--md-sys-color-error);
        background: var(--md-sys-color-error-container);
      }

      .error-icon {
        color: var(--md-sys-color-error) !important;
      }

      .version-error {
        margin: 4px 0 0 0;
        color: var(--md-sys-color-on-error-container);
        word-break: break-word;
      }

      @media (max-width: 640px) {
        .version-item.span-2 {
          grid-column: span 1;
        }

        .version-content {
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        }

        .version-action {
          width: 100%;
        }

        .version-action app-m3-button {
          width: 100%;
        }
      }

      .visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }
    `,
  ],
})
export class AppUpdateStatusComponent {
  appUpdatesStore = inject(AppUpdatesStore);
  private datePipe = new DatePipe('de-DE');

  updateStatus = computed<
    'disabled' | 'error' | 'downloading' | 'update-available' | 'no-update'
  >(() => {
    if (!this.appUpdatesStore.isEnabled()) return 'disabled';
    if (this.appUpdatesStore.hasError()) return 'error';
    if (this.appUpdatesStore.isDownloading()) return 'downloading';
    if (this.appUpdatesStore.isUpdateAvailable()) return 'update-available';
    return 'no-update';
  });

  downloadingLabel = computed(() => AppUpdatesLabels.downloadingUpdate());
  downloadingDescription = computed(() =>
    AppUpdatesLabels.downloadingUpdateDescription()
  );
  statusAnnouncement = computed(() => {
    if (this.isCheckForUpdateLoading()) {
      return AppUpdatesAria.updateCheckingStatus();
    }
    switch (this.updateStatus()) {
      case 'downloading':
        return AppUpdatesAria.updateDownloadingStatus();
      case 'error':
        return AppUpdatesAria.updateErrorStatus();
      case 'no-update':
        return AppUpdatesAria.noUpdateStatus();
      default:
        return '';
    }
  });

  lastCheckedText = computed(() => {
    const lastChecked = this.appUpdatesStore.lastChecked();
    return lastChecked
      ? this.datePipe.transform(lastChecked, 'dd.MM.yyyy, HH:mm')
      : 'nie';
  });

  currentVersionText = computed(() => {
    const version = this.appUpdatesStore.currentVersion();
    return version ? formatVersionHash(version) : '?';
  });

  latestVersionText = computed(() => {
    const version = this.appUpdatesStore.latestVersion();
    return version ? formatVersionHash(version) : '?';
  });

  isCheckForUpdateLoading = computed(() => {
    return this.appUpdatesStore.isCheckForUpdateLoading();
  });

  isActivateUpdateLoading = computed(() => {
    return this.appUpdatesStore.isActivateUpdateLoading();
  });
}
